import React from 'react';

import { useHasHpcMembers } from '../../hooks/useHasHpcMembers';
import { TrackingEventData } from '../../hooks/useTracking';
import { CheckboxGroup } from '../reusable/Checkbox';
import { RadioGroup } from '../reusable/Radio';
import { SizeProp } from '../reusable/util';
import {
  ContentLibraryFilterOption,
  useContentLibraryFilterContext,
} from './FilterContext';

interface ContentLibraryFiltersProps {
  trackingData?: TrackingEventData;
}

export const ContentLibraryFilters: React.FC<ContentLibraryFiltersProps> = ({
  trackingData,
}) => {
  const hasHpMembers = useHasHpcMembers();
  const { setAppliedFilters, appliedFilters, options, toggleFilter } =
    useContentLibraryFilterContext();

  const setRadioFilter = (
    filterValue: string,
    filterOptions: ContentLibraryFilterOption<unknown>[],
  ) => {
    const filterIsApplied = appliedFilters.all.some((filter) =>
      filterOptions.find((o) => o.value === filter.value),
    );

    if (filterIsApplied) {
      // Remove all filters from the given options, add the new one
      return setAppliedFilters((prev) => [
        ...prev.filter((f) => !filterOptions.some((o) => o.value === f)),
        filterValue,
      ]);
    }

    return setAppliedFilters((prev) => [...prev, filterValue]);
  };

  return (
    <>
      {hasHpMembers && (
        <>
          <RadioGroup
            size={SizeProp.Small}
            collapsible
            checkedOption={appliedFilters.performanceQuality?.value}
            label="Performance Quality"
            name="performanceQuality"
            onChange={(option) =>
              setRadioFilter(option.value, options.performanceQualities)
            }
            options={options.performanceQualities}
            trackingData={trackingData}
          />

          <CheckboxGroup
            size={SizeProp.Small}
            collapsible
            checkedOptions={appliedFilters.gameplanPrimaryComponents.map(
              (filter) => filter.value,
            )}
            label="Primary Gameplan Component"
            name="primaryGameplanComponent"
            onChange={(option) => toggleFilter(option.value)}
            options={options.gameplanPrimaryComponents}
            trackingData={trackingData}
          />

          <CheckboxGroup
            size={SizeProp.Small}
            collapsible
            checkedOptions={appliedFilters.gameplanPrimarySubcomponents.map(
              (filter) => filter.value,
            )}
            label="Primary Gameplan Subcomponent"
            name="primaryGameplanSubcomponent"
            onChange={(option) => toggleFilter(option.value)}
            options={options.gameplanPrimarySubcomponents}
            trackingData={trackingData}
          />

          <CheckboxGroup
            size={SizeProp.Small}
            collapsible
            checkedOptions={appliedFilters.gameplanSecondaryComponents.map(
              (filter) => filter.value,
            )}
            label="Secondary Gameplan Component"
            name="secondaryGameplanComponent"
            onChange={(option) => toggleFilter(option.value)}
            options={options.gameplanSecondaryComponents}
            trackingData={trackingData}
          />

          <CheckboxGroup
            size={SizeProp.Small}
            collapsible
            checkedOptions={appliedFilters.gameplanSecondarySubcomponents.map(
              (filter) => filter.value,
            )}
            label="Secondary Gameplan Subcomponent"
            name="secondaryGameplanSubcomponent"
            onChange={(option) => toggleFilter(option.value)}
            options={options.gameplanSecondarySubcomponents}
            trackingData={trackingData}
          />

          <CheckboxGroup
            size={SizeProp.Small}
            collapsible
            checkedOptions={appliedFilters.associatedProject.map(
              (filter) => filter.value,
            )}
            label="Associated Project"
            name="associatedProject"
            onChange={(option) => toggleFilter(option.value)}
            options={options.associatedProject}
            trackingData={trackingData}
          />
        </>
      )}

      <CheckboxGroup
        size={SizeProp.Small}
        collapsible
        checkedOptions={appliedFilters.formats.map((filter) => filter.value)}
        label="Format"
        name="format"
        onChange={(option) => toggleFilter(option.value)}
        options={options.formats}
        trackingData={trackingData}
      />

      <CheckboxGroup
        size={SizeProp.Small}
        collapsible
        checkedOptions={appliedFilters.genres.map((filter) => filter.value)}
        label="Genre"
        name="genre"
        onChange={(option) => toggleFilter(option.value)}
        options={options.genres}
        trackingData={trackingData}
      />

      {hasHpMembers && (
        <CheckboxGroup
          size={SizeProp.Small}
          collapsible
          checkedOptions={appliedFilters.intensities.map(
            (filter) => filter.value,
          )}
          label="Intensity"
          name="intensity"
          onChange={(option) => toggleFilter(option.value)}
          options={options.intensities}
          trackingData={trackingData}
        />
      )}

      <CheckboxGroup
        size={SizeProp.Small}
        collapsible
        checkedOptions={appliedFilters.levels.map((filter) => filter.value)}
        label="Difficulty"
        name="difficulty"
        onChange={(option) => toggleFilter(option.value)}
        options={options.levels}
        trackingData={trackingData}
      />

      <CheckboxGroup
        size={SizeProp.Small}
        collapsible
        checkedOptions={appliedFilters.splits.map((filter) => filter.value)}
        label="Split"
        name="split"
        onChange={(option) => toggleFilter(option.value)}
        options={options.splits}
        trackingData={trackingData}
      />

      <RadioGroup
        size={SizeProp.Small}
        collapsible
        checkedOption={appliedFilters.duration?.value}
        label="Duration"
        name="duration"
        onChange={(option) => setRadioFilter(option.value, options.durations)}
        options={options.durations}
        trackingData={trackingData}
      />

      <RadioGroup
        size={SizeProp.Small}
        collapsible
        checkedOption={appliedFilters.isExperiential?.value}
        label="Is Experiential"
        name="experiential"
        onChange={(option) =>
          setRadioFilter(option.value, options.isExperiential)
        }
        options={options.isExperiential}
        trackingData={trackingData}
      />
    </>
  );
};
